import { Vue } from 'vue-property-decorator';
import { VNode, DirectiveOptions, VNodeDirective } from 'vue';
import { BoIdMapping } from '@/types/BoIdMapping';
import { formatImageUrl, getProperSize } from '@/helpers/images';
import { findInParents } from '../helpers/objects';
import { BlockDTO } from '../model/BlockDTO';
import { ImageResourceDTO } from '../model/ImageResourceDTO';

import { isEqual } from 'lodash-es';

export interface WmsResourceProps {
	boIdMapping?: BoIdMapping;
	params?: any[];
}

function bind(el: HTMLElement, binding: VNodeDirective, vnode: VNode, oldVnode: VNode) {
	const resourceType = binding.arg;

	if (binding.value === binding.oldValue && isEqual(vnode.data.attrs, oldVnode.data.attrs)) {
		// no attributes changed
		return;
	}

	unbind(el);

	const {context, data: {attrs, on: events}} = vnode;

	switch(resourceType) {
		case 'text':
			if (binding.modifiers.value) {
				bindTextValue(binding.value, el, context, attrs, events);
			} else {
				bindText(binding.value, el, context, attrs, events);
			}
			break;
		case 'image':
			if (binding.modifiers.value) {
				bindImageValue(binding.value, el, context, attrs, events);
			} else {
				bindImage(binding.value, el, context, attrs, events);
			}
			break;
		default:
			throw new Error(`unknown resource type '${resourceType}'`);
	}
}

function bindText(resourceKey: string, el: HTMLElement, context: Vue, attrs: {[key: string]: any} = {}, events: {[key: string]: any} = {}) {
	let {boIdMapping, params}: WmsResourceProps = attrs;

	const block = findInParents(context, '$parent', 'block') as BlockDTO;
	boIdMapping = boIdMapping || findInParents(context, '$parent', 'boIdMapping') as BoIdMapping;

	const unwatch = context.$watch(function () {
		return this.$store.getText(resourceKey, block.id, params, boIdMapping);
	}, (text: string) => {
		applyTextToElement(text, el);
		events.resourceUpdated && events.resourceUpdated(el);
	}, {
		immediate: true
	});

	(el as any).unbind = (el: HTMLElement) => {
		unwatch();
		removeTextFromElement(el);
	};
}

function bindTextValue(value: string, el: HTMLElement, context: Vue, attrs: {[key: string]: any} = {}, events: {[key: string]: any} = {}) {
	applyTextToElement(value, el);
	events.resourceUpdated && events.resourceUpdated(el);

	(el as any).unbind = (el: HTMLElement) => {
		removeTextFromElement(el);
	};
}

function applyTextToElement(text: string, el: HTMLElement) {
	el.innerHTML = text;

	const scripts = Array.from(el.getElementsByTagName('script'));

	for (let script of scripts) {
		const newScript = document.createElement('script');
		newScript.type = 'text/javascript';

		if (script.attributes) {
			Array.from(script.attributes).forEach(attr => {
				newScript.setAttribute(attr.name, attr.value);
			})
		}

		if (!script.src) {
			newScript.textContent = script.textContent;
		}

		el.removeChild(script);
		el.appendChild(newScript);
	}
}

function removeTextFromElement(el: HTMLElement) {
	el.innerHTML = "";
}

async function bindImage(resourceKey: string, el: HTMLElement, context: Vue, attrs: {[key: string]: any} = {}, events: {[key: string]: any} = {}) {
	let {size, boIdMapping, sizes} = attrs;

	checkImageElement(el);

	if (el.tagName.toLowerCase() === 'img') {
		// hide image before it's loaded, because the 'alt' attribute is shown when 'src' is not set
		(el as HTMLImageElement).style.visibility = 'hidden';
	}

	const block = findInParents(context, '$parent', 'block') as BlockDTO;
	boIdMapping = boIdMapping || findInParents(context, '$parent', 'boIdMapping') as BoIdMapping;

	const unwatch = context.$watch(function () {
		return [this.$store.langId, this.$store.timeId];
	}, async () => {
		const resources = await context.$resourceService.fetchImages(
			block.id,
			resourceKey,
			boIdMapping || context.$store.boIdMapping
		);

		applyImageToElement(el, resources[0], size, sizes);
		(el as HTMLImageElement).style.visibility = 'visible';

		events.resourceUpdated && events.resourceUpdated(el);
	}, {
		immediate: true,
		deep: true
	});

	(el as any).unbind = (el: HTMLElement) => {
		unwatch();

		removeImageFromElement(el);
	};
}

function bindImageValue(value: ImageResourceDTO[][], el: HTMLElement, context: Vue, attrs: {[key: string]: any} = {}, events: {[key: string]: any} = {}) {
	let {size, sizes} = attrs;

	checkImageElement(el);

	applyImageToElement(el, value[0], size, sizes);
	events.resourceUpdated && events.resourceUpdated(el);

	(el as any).unbind = (el: HTMLElement) => {
		removeImageFromElement(el);
	};
}

function checkImageElement(el: HTMLElement) {
	const tag = el.tagName.toLowerCase();

	const allowedTags = ['img', 'a'];
	if (! allowedTags.includes(tag)) {
		throw new Error(`Image resource is allowed only for tags ${allowedTags.map((tag) => `<${tag}>`).join(', ')}`);
	}
}

function applyImageToElement(el: HTMLElement, imageResource: ImageResourceDTO[], size: number, sizes: string) {
	if (! imageResource) {
		return;
	}

	switch (el.tagName.toLowerCase()) {
		case 'img':
			(el as HTMLImageElement).src = formatImageUrl(getProperSize(imageResource, 1000).path);
			(el as HTMLImageElement).setAttribute("srcset", imageResource.map(r => `${formatImageUrl(r.path)} ${r.width}w`).join(", "));
			(el as HTMLImageElement).setAttribute("sizes", sizes);
			const resourceImageWithDescription = imageResource.find(r => !!r.description);
			if (resourceImageWithDescription) {
				(el as HTMLImageElement).alt = resourceImageWithDescription.description;
			}
		break;
		case 'a':
			const image = getProperSize(imageResource, size || 1200);
			(el as HTMLAnchorElement).href = formatImageUrl(image.path);
			if (image.width && image.height) {
				if (!(el as HTMLAnchorElement).dataset.fancybox) {
					(el as HTMLAnchorElement).dataset.fancybox = "";
				}
				(el as HTMLAnchorElement).dataset.width = image.width.toString();
				(el as HTMLAnchorElement).dataset.height = image.height.toString();
			}
		break;
	}
}

function removeImageFromElement(el: HTMLElement) {
	switch (el.tagName.toLowerCase()) {
		case 'img':
			(el as HTMLImageElement).src = "";
			(el as HTMLImageElement).setAttribute("srcset", "");
			(el as HTMLImageElement).setAttribute("sizes", "");
			(el as HTMLImageElement).alt = "";
		break;
		case 'a':
			(el as HTMLAnchorElement).href = "";
		break;
	}
}

function unbind(el: HTMLElement) {
	const unbind = (el as any).unbind;
	delete (el as any).unbind;
	unbind && unbind(el);
}

export const WmsResource: DirectiveOptions = {
	bind,
	update: bind,
	unbind
};
